var pym = require('./lib/pym');
var ANALYTICS = require('./lib/analytics');
require('./lib/webfonts');
var { isMobile } = require('./lib/breakpoints');
var { flow, map, mapValues, omitBy, forEach } = require('lodash/fp');
var pymChild;

var textures = require('textures');

var {
  COLORS,
  classify,
  makeTranslate,
  parseNumber,
  wrapText
} = require('./lib/helpers');

var d3 = Object.assign(
  {},
  require('d3-axis'),
  require('d3-scale'),
  require('d3-selection'),
  require('d3-shape'),
  require('d3-interpolate')
  // require('d3-svg-annotation')
);

// https://github.com/wbkd/d3-extended
d3.selection.prototype.moveToFront = function() {
  return this.each(function() {
    this.parentNode.appendChild(this);
  });
};
d3.selection.prototype.moveToBack = function() {
  return this.each(function() {
    var firstChild = this.parentNode.firstChild;
    if (firstChild) {
      this.parentNode.insertBefore(this, firstChild);
    }
  });
};

var fmtYearAbbrev = d => (d.getFullYear() + '').slice(-2);
var fmtYearFull = d => d.getFullYear();

//Initialize graphic
var onWindowLoaded = function() {
  render();

  window.addEventListener('resize', render);

  pym.then(child => {
    pymChild = child;
    child.sendHeight();

    pymChild.onMessage('on-screen', function(bucket) {
      ANALYTICS.trackEvent('on-screen', bucket);
    });
    pymChild.onMessage('scroll-depth', function(data) {
      data = JSON.parse(data);
      ANALYTICS.trackEvent('scroll-depth', data.percent, data.seconds);
    });
  });
};

//Format graphic data for processing by D3.
var formatData = function(data) {
  data.forEach(function(d) {
    try {
      d.date = new Date(d.date);
    } catch (err) {
      d.meta = d.date;
      delete d.date;
    }
  });

  // Restructure for easier calculations
  data.forEach(function(d) {
    d.values = {};

    for (let [key, val] of Object.entries(d)) {
      switch (key) {
        case 'date':
        case 'degree':
        case 'values':
          break;

        default:
          d.values[key] = +val;
      }
    }
  });
};

// Render the graphic(s). Called by pym with the container width.

var charts = COPY.data.reduce(function(r, a) {
  // get current degree key contents
  r[a.degree] = r[a.degree] || [];

  // push current item to degree key
  r[a.degree].push(a);

  return r;
}, Object.create(null));

var chartEntries = Object.entries(charts);

chartEntries.map(([_, data]) => formatData(data));

var render = function() {
  const parseValue = d => {
    switch (d.type) {
      case 'number':
        return parseNumber(d.use_value);
      default:
        return d.use_value;
    }
  };

  var chartObjects = d3
    .select('#graphic-container')
    .selectAll('.line-chart-container')
    .data(chartEntries)
    .enter()
    .append('div')
    .attr('class', ([title, _]) => `line-chart-container ${classify(title)}`);

  chartObjects
    .append('div')
    .classed('line-chart-title', true)
    .text(([title, _]) => title);

  chartObjects.each(function([title, data]) {
    var slug = classify(title);

    var chartElement = d3
      .select(this)
      .append('div')
      .attr('id', `line-chart-${slug}`)
      .attr('class', 'line-chart');

    var width = chartElement.node().getBoundingClientRect().width;

    var IS_MOBILE = width < 500;

    const loadMobile = d => {
      if (d.value_mobile && IS_MOBILE) {
        d.use_value = d.value_mobile;
      } else {
        d.use_value = d.value;
      }

      return d;
    };

    var props = flow(
      mapValues(loadMobile),
      // mapValues(parseValue),
      omitBy(d => d == null)
    )(PROPS);

    var dataMax = Math.max(
      ...data.map(d => Object.values(d.values).reduce((a, b) => a + b))
    );
    // var yMax = Math.round(Math.min(props.yMax, dataMax), 200);
    // var yRatio = yMax / 100;

    // Object.assign(props, { yMax, yRatio });

    renderLineChart({
      container: `#line-chart-${slug}`,
      width,
      data,
      props
    });
  });

  // Update iframe
  if (pymChild) {
    pymChild.sendHeight();
  }
};

// Render a line chart.
var renderLineChart = function(config) {
  var {
    dateColumn,
    /* axes */
    ticksX,
    ticksY,
    roundTicksFactor,
    yMin,
    yMax,
    /* margins */
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    /* grid */
    showXAxisGrid,
    showYAxisGrid,
    labelWidth,
    xAxisTickValues,
    /* chart */
    maxChartHeight,
    yRatio = 1
  } = config.props;

  // Setup
  var IS_MOBILE = config.width < 500;

  var aspectWidth = 3;
  var aspectHeight = 4;

  var margins = {
    top: marginTop,
    right: marginRight,
    bottom: marginBottom,
    left: marginLeft
  };

  // Calculate actual chart dimensions
  var chartWidth = config.width - margins.left - margins.right;
  var chartHeight =
    Math.ceil((config.width * aspectHeight) / aspectWidth) -
    margins.top -
    margins.bottom;

  // chartHeight = chartHeight * yRatio;

  // Clear existing graphic (for redraw)
  var containerElement = d3.select(config.container);
  containerElement.html('');

  var dates = config.data.map(d => d[dateColumn]);
  var extent = [dates[0], dates[dates.length - 1]];

  var xScale = d3
    .scaleTime()
    .domain(extent)
    .range([0, chartWidth]);

  var yScale = d3
    .scaleLinear()
    .domain([yMin, yMax])
    .range([chartHeight, 0]);

  var colorScale = d3
    .scaleOrdinal()
    .domain(Object.keys(config.data[0].values))
    .range(['#d94538', '#a9a9a9']);

  // Create the root SVG element.

  var svg = containerElement
    .append('svg')
    .attr('width', chartWidth + margins.left + margins.right)
    .attr('height', chartHeight + margins.top + margins.bottom);

  var chartElement = svg
    .append('g')
    .attr('transform', `translate(${margins.left},${margins.top})`);

  // Create D3 axes.

  var xAxis = d3
    .axisBottom()
    .scale(xScale)
    .ticks(ticksX)
    .tickFormat(function(d, i) {
      if (IS_MOBILE) {
        return '\u2019' + fmtYearAbbrev(d);
      } else {
        return fmtYearFull(d);
      }
    })
    .tickValues(
      xAxisTickValues && xAxisTickValues.split(', ').map(d => new Date(d))
    );

  var yAxis = d3
    .axisLeft()
    .scale(yScale)
    .ticks(ticksY)
    .tickFormat((d, i, ticks) => {
      if (i === ticks.length - 1) return d + '%';
      else return d;
    });

  // Render axes to chart.

  chartElement
    .append('g')
    .attr('class', 'x axis')
    .attr('transform', makeTranslate(0, chartHeight))
    .call(xAxis);

  chartElement
    .append('g')
    .attr('class', 'y axis')
    .call(yAxis);

  // Render grid to chart.

  if (showXAxisGrid) {
    var xAxisGrid = function() {
      return xAxis;
    };

    chartElement
      .append('g')
      .attr('class', 'x grid')
      .attr('transform', makeTranslate(0, chartHeight))
      .call(
        xAxisGrid()
          .tickSize(-chartHeight, 0, 0)
          .tickFormat('')
      );
  }

  if (showYAxisGrid) {
    var yAxisGrid = function() {
      return yAxis;
    };

    chartElement
      .append('g')
      .attr('class', 'y grid')
      .call(
        yAxisGrid()
          .tickSize(-chartWidth, 0, 0)
          .tickFormat('')
      );
  }

  // Render 0 value line.

  if (yMin < 0) {
    chartElement
      .append('line')
      .attr('class', 'zero-line')
      .attr('x1', 0)
      .attr('x2', chartWidth)
      .attr('y1', yScale(0))
      .attr('y2', yScale(0));
  }

  var dataSeries = [];

  ['Statewide', 'Chicago'].forEach(name => {
    dataSeries.push({
      name,
      values: config.data.map(d => ({
        date: d.date,
        amt: d[name]
      }))
    });
  });

  var line = d3
    .line()
    .x(d => xScale(d[dateColumn]))
    .y(d => yScale(d['amt']))
    .curve(d3.curveBasis);

  var series = chartElement
    .append('g')
    .attr('class', 'lines')
    .selectAll('.line')
    .data(dataSeries)
    .enter()
    .append('g')
    .attr('class', d => 'line ' + classify(d.name));

  series
    .append('path')
    .attr('fill', 'transparent')
    .attr('stroke', d => colorScale(d.name))
    .attr('d', d => line(d.values));

  series.append('text').text(d => d.key);

  // series
  //   .selectAll('text')
  //   .datum(getLabelPosition)
  //   .classed('hidden', d => !d)
  //   .filter(d => d)
  //   .attr('x', d => d[0])
  //   .attr('y', d => d[1])
  //   .attr('dx', 5)
  //   .attr('dy', 5)
  //   .call(wrapText, labelWidth, 13);

  function getLabelPosition(points) {
    // Eventually...
    // https://bl.ocks.org/veltman/0f7ed47d7839ba0afa8d23414aeb8933
    var bbox = this.getBBox();

    var d = points.slice(-1)[0];
    var floor = yScale(d[0]);
    var ceiling = yScale(d[1]);

    var bestPoint;

    if (floor - ceiling > bbox.height - 8) {
      bestPoint = [xScale.range()[1], (floor + ceiling) / 2];
    }

    return bestPoint;
  }

  // Render annotation group
  const annotation = chartElement.append('g').attr('class', 'annotation-group');

  const texture = textures
    .lines()
    .size(6)
    .strokeWidth(1)
    .stroke('#bbb');

  svg.call(texture);

  annotation
    .append('rect')
    .attr('x', xScale(new Date('1/1/2010')))
    .attr('y', 0)
    .attr('width', xScale(new Date('1/1/2016')) - xScale(new Date('1/1/2010')))
    .attr('height', chartHeight)
    .style('fill', texture.url());

  annotation
    .append('text')
    .classed('annotation-note', true)
    .text('Reinvention')
    .style('text-anchor', 'start')
    .style(
      'transform',
      `rotate(-90deg) translateY(${xScale(new Date('1/1/2010')) -
        5}px) translateX(${-chartHeight + 10}px)`
    )
    .style('font-weight', 'bold');

  chartElement.select('.lines').moveToFront();
};

//Initially load the graphic
// (NB: Use window.load to ensure all images have loaded)
window.onload = onWindowLoaded;
